import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Ban,
  Building2,
  ClipboardList,
  ExternalLink,
  Loader2,
  PackageCheck,
  TriangleAlert,
  Truck,
  Warehouse,
} from 'lucide-react';
import { DetailBreadcrumb } from '../../../../components/ui/DetailBreadcrumb';
import { DetailHero } from '../../../../components/ui/DetailHero';
import { PrintLabelsButton } from './components/PrintLabelsButton';
import { DataTable } from '../../../../components/ui/DataTable';
import { StatCard } from '../../../../components/ui/StatCard';
import { Button } from '../../../../components/ui/Button';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';
import { ErrorState } from '../../../../components/ui/ErrorState';
import { StaleDataNotice } from '../../../../components/ui/StaleDataNotice';
import { useNotice } from '../../../../hooks/useNotice';
import { StatusBadge } from '../../../../components/deliveries/StatusBadge';
import { cancelWmsOrder, fetchWmsOrder } from '../../../../features/wmsOrders/wmsOrdersThunks';
import { clearWmsOrderDetail } from '../../../../features/wmsOrders/wmsOrdersSlice';
import {
  cancelConsequences,
  cancelDoneNotice,
  formatOrderDate,
  formatOrderMoment,
  isCancellableState,
  isOpenState,
  orderTitle,
} from '../../../../features/wmsOrders/wmsOrdersUtils';
import {
  courierSummaryOf,
  labelResultUrls,
  packageSuppliesSummary,
} from '../../../../features/fulfillment/fulfillmentUtils';
import { formatCourier } from '../../../../features/deliveries/deliveriesUtils';
import { toPublicLabelUrl } from '../../../../utils/s3Utils';
import { selectCanWms } from '../../../../features/auth/authSelectors';
import {
  formatQuantity,
  moduleInactiveHint,
  moduleInactiveLabel,
  orderSourceLabel,
  wmsOrderStateColor,
  wmsOrderStateLabel,
} from '../../../../utils/inventoryLabels';

const ORDERS_PATH = '/warehouse/orders';

const selectCanCancel = selectCanWms('order:cancel');
const selectCanFulfill = selectCanWms('order:fulfill');

const Fact = ({ label, children }) => (
  <div className="space-y-1 min-w-0">
    <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">{label}</span>
    <p className="font-medium text-ninja-dark dark:text-white break-words">{children || '-'}</p>
  </div>
);

const Note = ({ tone, icon: Icon, testId, children }) => (
  <div
    data-testid={testId}
    className={
      tone === 'danger'
        ? 'flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900 rounded-xl px-4 py-3 text-sm text-ninja-dark dark:text-white'
        : 'flex items-start gap-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-900 rounded-xl px-4 py-3 text-sm text-ninja-dark dark:text-white'
    }
  >
    <Icon size={16} className={tone === 'danger' ? 'text-danger flex-shrink-0 mt-0.5' : 'text-warning flex-shrink-0 mt-0.5'} />
    <div className="space-y-1">{children}</div>
  </div>
);

/**
 * `/warehouse/orders/:orderId`: one order, and what its warehouse state was
 * deduced from.
 *
 * The state is a word the server works out; this page shows the numbers
 * behind it — how much was reserved, how much prepared on each line, which
 * parcel is still open — because when the word surprises, the reason is the
 * first thing anybody wants to see.
 *
 * Preparing (picking, parcels, labelling and dispatching) is a task and has its
 * own screen; this page is the door to it.
 */
export const WarehouseOrderDetail = () => {
  const { orderId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { detail, detailLoading, detailError, cancellingId } = useSelector(
    (state) => state.wmsOrders
  );
  const canCancel = useSelector(selectCanCancel);
  const canFulfill = useSelector(selectCanFulfill);

  const [isCancelling, setIsCancelling] = useState(false);
  const setNotice = useNotice('order-detail-notice');

  const loadOrder = useCallback(() => dispatch(fetchWmsOrder(orderId)), [dispatch, orderId]);

  useEffect(() => {
    loadOrder();
    return () => dispatch(clearWmsOrderDetail());
  }, [loadOrder, dispatch]);


  // The id is compared too: while the next order loads, the previous one is
  // still in the store and must not be drawn under the new address.
  const isCurrent = Boolean(detail) && String(detail.id) === String(orderId);

  // A failed reload of the order on screen leaves it there, under a strip.
  if (detailError && !isCurrent) {
    return (
      <ErrorState title="No se pudo abrir la orden" description={detailError} onRetry={loadOrder}>
        <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate(ORDERS_PATH)}>
          Volver a Órdenes
        </Button>
      </ErrorState>
    );
  }

  if (detailLoading || !isCurrent) {
    return (
      <div data-testid="order-detail-loading" className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-ninja-green animate-spin" />
      </div>
    );
  }

  const order = detail;
  const canCancelThis =
    canCancel && order.module_active !== false && isCancellableState(order.wms_state);
  const canFulfillThis =
    canFulfill && order.module_active !== false && isOpenState(order.wms_state);

  const handleCancel = async () => {
    const action = await dispatch(cancelWmsOrder(order.id));
    setIsCancelling(false);
    if (action.meta.requestStatus === 'fulfilled') {
      setNotice(cancelDoneNotice(action.payload));
      // The lines and the history changed with it.
      loadOrder();
    } else {
      setNotice({ type: 'error', message: action.payload });
    }
  };

  const lineColumns = [
    {
      header: 'PRODUCTO',
      cell: (row) => (
        <div className="flex flex-col max-w-[320px]">
          <span className="font-bold font-mono text-ninja-dark dark:text-white">{row.sku || '-'}</span>
          <span className="text-xs text-text-muted mt-0.5 truncate" title={row.name}>
            {row.name || '-'}
          </span>
        </div>
      ),
    },
    {
      header: 'PEDIDO',
      cell: (row) => <span className="tabular-nums">{formatQuantity(row.quantity)}</span>,
    },
    {
      // What could not be reserved is why an order sits in "Ingresada" with
      // stock on the shelf: the number is shown, not a generic warning.
      header: 'RESERVADO',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="tabular-nums">{formatQuantity(row.reserved_quantity)}</span>
          {Number(row.pending_reservation) > 0 && (
            <span
              data-testid="order-line-pending"
              title="No alcanzó el stock disponible para reservar todo. Se completa sola cuando entre mercadería."
              className="text-xs text-warning mt-0.5"
            >
              Faltan {formatQuantity(row.pending_reservation)} por reservar
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'PREPARADO',
      cell: (row) => (
        <span className="font-semibold tabular-nums">{formatQuantity(row.prepared_quantity)}</span>
      ),
    },
  ];

  // Every label the courier left, for the button under the parcels.
  const labelUrls = labelResultUrls(courierSummaryOf(order, order?.packages));

  const packageColumns = [
    {
      header: 'BULTO',
      cell: (row) => (
        <span className="font-semibold">
          {row.item_index} de {row.total_items}
        </span>
      ),
    },
    {
      header: 'PESO Y MEDIDAS',
      cell: (row) =>
        row.weight_grams || row.height_cm ? (
          <div className="flex flex-col">
            <span className="tabular-nums">{row.weight_grams ? `${row.weight_grams} g` : '-'}</span>
            <span className="text-xs text-text-muted mt-0.5">
              {[row.height_cm, row.width_cm, row.length_cm].every(Boolean)
                ? `${row.height_cm} × ${row.width_cm} × ${row.length_cm} cm`
                : 'Sin medidas'}
            </span>
          </div>
        ) : (
          <span className="text-text-muted">Sin peso ni medidas</span>
        ),
    },
    {
      // What the parcel was built with. It is what gets charged for the packing.
      header: 'INSUMOS',
      cell: (row) =>
        (row.supplies || []).length > 0 ? (
          <span data-testid="order-package-supplies" className="text-sm">
            {packageSuppliesSummary(row)}
          </span>
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
    {
      header: 'SEGUIMIENTO',
      cell: (row) => <span className="font-mono">{row.tracking_number || '-'}</span>,
    },
    {
      header: 'ETIQUETA',
      cell: (row) =>
        row.label ? (
          <a
            href={toPublicLabelUrl(row.label)}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-ninja-green hover:underline"
            data-testid="order-package-label"
          >
            Ver etiqueta
          </a>
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
    {
      header: 'ESTADO',
      cell: (row) => (
        <ToneBadge tone={row.complete ? 'success' : 'warning'} testId="order-package-state">
          {row.complete ? 'Cerrado' : 'Incompleto'}
        </ToneBadge>
      ),
    },
  ];

  return (
    <div data-testid="order-detail-page" className="space-y-6 animate-in fade-in duration-300">
      <DetailBreadcrumb backLabel="Volver a Órdenes" to={ORDERS_PATH} current={orderTitle(order)} />

      {detailError && (
        <StaleDataNotice testId="order-detail-stale-error" error={detailError} onRetry={loadOrder} />
      )}

      <DetailHero
        media={<ClipboardList size={36} />}
        title={orderTitle(order)}
        badges={
          <ToneBadge tone={wmsOrderStateColor(order.wms_state)} testId="order-state">
            {wmsOrderStateLabel(order.wms_state)}
          </ToneBadge>
        }
        meta={
          <>
            {order.company && (
              <span className="flex items-center gap-1.5">
                <Building2 size={14} /> {order.company.name}
              </span>
            )}
            {order.warehouse && (
              <span className="flex items-center gap-1.5">
                <Warehouse size={14} /> {order.warehouse.name}
              </span>
            )}
            <span>{orderSourceLabel(order.source)}</span>
            <span>Creada el {formatOrderDate(order.created_at)}</span>
          </>
        }
        actions={
          <>
            {order.url_tracking && (
              <Button
                variant="ghost"
                icon={Truck}
                iconRight={ExternalLink}
                onClick={() => window.open(order.url_tracking, '_blank', 'noopener,noreferrer')}
                data-testid="order-tracking"
              >
                Seguimiento
              </Button>
            )}
            {canCancelThis && (
              <Button
                variant="danger"
                icon={Ban}
                onClick={() => setIsCancelling(true)}
                data-testid="order-cancel"
              >
                Cancelar orden
              </Button>
            )}
            {canFulfillThis && (
              <Button
                variant="primary"
                icon={PackageCheck}
                onClick={() => navigate(`${ORDERS_PATH}/${order.id}/fulfill`)}
                data-testid="order-fulfill"
              >
                Preparar
              </Button>
            )}
          </>
        }
      />

      {order.closed_badly && (
        <Note tone="warning" icon={TriangleAlert} testId="order-closed-badly">
          <p>Esta orden salió del flujo de bodega. No hay nada que preparar.</p>
        </Note>
      )}

      {order.module_active === false && (
        <Note tone="danger" icon={TriangleAlert} testId="order-module-off">
          <p>
            En <strong>{order.warehouse?.name}</strong>, {order.company?.name}{' '}
            <strong>{moduleInactiveLabel(order.module_inactive_reason)}</strong>. La orden se ve,
            pero no se puede reservar, pickear ni despachar desde acá hasta que eso cambie.
          </p>
          {moduleInactiveHint(order.module_inactive_reason) && (
            <p className="text-xs text-text-secondary">
              {moduleInactiveHint(order.module_inactive_reason)}
            </p>
          )}
        </Note>
      )}

      {order.module_active !== false && !order.processable && isOpenState(order.wms_state) && (
        <Note tone="warning" icon={TriangleAlert} testId="order-short-stock">
          <p>
            No alcanzó el stock disponible para reservar todo lo pedido. Se completa sola cuando
            entre mercadería.
          </p>
        </Note>
      )}

      {/* The numbers the state was deduced from. */}
      <div data-testid="order-figures" className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard title="Pedido" value={formatQuantity(order.requested_units || 0)} subtitle="Unidades en las líneas" />
        <StatCard
          title="Reservado"
          value={formatQuantity(order.reserved_units || 0)}
          tone={Number(order.reserved_units) > 0 ? 'warning' : undefined}
          subtitle="Prometido a esta orden"
        />
        <StatCard
          title="Preparado"
          value={formatQuantity(order.prepared_units || 0)}
          tone={Number(order.prepared_units) > 0 ? 'success' : undefined}
          subtitle="Pickeado, en la mesa"
        />
        <StatCard
          title="Bultos"
          value={String(order.packages_count || 0)}
          subtitle={order.has_label ? 'Con etiqueta del courier' : order.is_packed ? 'Cerrados' : 'Por armar'}
        />
      </div>

      <section className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          <Fact label="Destinatario">{order.recipient}</Fact>
          <Fact label="Contacto">
            {[order.contact_phone, order.contact_email].filter(Boolean).join(' · ') || '-'}
          </Fact>
          <Fact label="Courier">
            {[order.courier && formatCourier(order.courier), order.shipping_method]
              .filter(Boolean)
              .join(' · ') || '-'}
          </Fact>
          <Fact label="Seguimiento">
            {order.url_tracking ? (
              <a
                href={order.url_tracking}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-ninja-green hover:underline"
                data-testid="order-tracking-link"
              >
                {order.tracking_number || 'Ver en el courier'}
              </a>
            ) : (
              <span className="font-mono">{order.tracking_number || '-'}</span>
            )}
          </Fact>
          <Fact label="Estado del envío">
            {order.delivery_status ? (
              <StatusBadge status={order.delivery_status} variant="inline" testId="order-delivery-status" />
            ) : (
              'Sin envío'
            )}
          </Fact>
          <Fact label="Estado de la orden">{order.order_status_display}</Fact>
          <Fact label="Fecha prometida">{formatOrderDate(order.promised_date)}</Fact>
          <Fact label="Creada">{formatOrderMoment(order.created_at)}</Fact>
        </div>
        {order.notes && (
          <p data-testid="order-notes" className="text-sm text-text-secondary mt-6">
            <span className="font-semibold text-ninja-dark dark:text-white">Notas:</span> {order.notes}
          </p>
        )}
      </section>

      <div className="space-y-3">
        <h2 className="text-lg font-bold text-ninja-dark dark:text-white">Productos</h2>
        {!order.has_lines && (
          <p data-testid="order-no-lines" className="text-sm text-text-secondary">
            Este pedido entró sin detalle de productos, y por eso no hay nada que reservar ni
            pickear: su estado sale del envío. Los pedidos cargados por planilla y por el formulario
            manual entran así.
          </p>
        )}
        <DataTable
          columns={lineColumns}
          data={order.lines || []}
          totalItems={(order.lines || []).length}
          currentPage={1}
          itemsPerPage={100}
          itemLabel="líneas"
          emptyMessage="Sin líneas de producto."
        />
      </div>

      <div className="space-y-3">
        <h2 className="text-lg font-bold text-ninja-dark dark:text-white">Bultos</h2>
        <DataTable
          columns={packageColumns}
          data={order.packages || []}
          totalItems={(order.packages || []).length}
          currentPage={1}
          itemsPerPage={100}
          itemLabel="bultos"
          emptyMessage="Todavía no hay bultos armados."
        />
        {labelUrls.length > 0 && (
          <PrintLabelsButton
            urls={labelUrls}
            testId="order-labels-print"
            label="Imprimir todas las etiquetas"
          />
        )}
      </div>

      <section
        data-testid="order-history"
        className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border"
      >
        <h2 className="text-lg font-bold text-ninja-dark dark:text-white">Historial</h2>
        <p className="text-sm text-text-secondary mt-1 mb-4">
          Sale del timeline del envío, del de la orden y de los movimientos del kardex.
        </p>
        {(order.history || []).length === 0 ? (
          <p className="text-sm text-text-muted">Todavía no hay movimientos registrados.</p>
        ) : (
          <ul className="divide-y divide-border">
            {order.history.map((event, index) => (
              <li
                key={`${event.kind}-${index}`}
                data-testid="order-event"
                className="py-3 flex flex-col sm:flex-row sm:items-baseline gap-x-4 gap-y-1"
              >
                <span className="text-xs text-text-muted sm:w-36 flex-shrink-0">
                  {formatOrderMoment(event.at)}
                </span>
                <span className="text-sm font-semibold text-ninja-dark dark:text-white sm:w-40 flex-shrink-0">
                  {event.label}
                </span>
                <span className="text-sm text-text-secondary flex-1 min-w-0 break-words">
                  {event.detail}
                </span>
                {event.actor && <span className="text-xs text-text-muted">{event.actor}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <ConfirmDialog
        isOpen={isCancelling}
        onClose={() => setIsCancelling(false)}
        onConfirm={handleCancel}
        title={`Cancelar la orden ${orderTitle(order)}`}
        message={cancelConsequences(order).join(' ')}
        confirmLabel="Cancelar orden"
        loadingLabel="Cancelando..."
        loading={cancellingId === order.id}
        testId="order-cancel-dialog"
      />

    </div>
  );
};
